import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, CheckCircle, AlertCircle, BookOpen, Users, Award } from 'lucide-react';
import { trackTrainingRequest } from '../utils/analytics';
import { HoneypotField } from '../components/HoneypotField';

const API = '/api';

export const CustomizedTrainingRequestPage = () => {
  const { t, direction } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_person: '',
    email: '',
    phone: '',
    country: '',
    industry: '',
    topics: '',
    audience_size: '',
    preferred_dates: '',
    preferred_location: '',
    language: 'english',
    budget_min: '',
    budget_max: '',
    currency: 'EUR',
    training_goals: '',
    notes: '',
    website: '',  // Honeypot field
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        topics: formData.topics.split(',').map(t => t.trim()).filter(Boolean),
        audience_size: formData.audience_size ? parseInt(formData.audience_size) : null,
        budget_min: formData.budget_min ? parseInt(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseInt(formData.budget_max) : null,
      };
      
      const response = await fetch(`${API}/customized-training-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        setSuccess(true);
        // Track training request in Google Analytics
        trackTrainingRequest();
      } else {
        const data = await response.json();
        setError(data.detail || t('forms.error'));
      }
    } catch (error) {
      setError(t('forms.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#111827] mb-2">{t('forms.success')}</h2>
            <p className="text-[#6B7280] mb-6">
              Thank you for your training request! Our team will analyze your needs and prepare a customized proposal.
            </p>
            <Link to="/">
              <Button className="btn-primary">{t('common.back')} to {t('nav.home')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="customized-training-request-page">
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#6B7280] hover:text-[#33AADD] mb-6"
            data-testid="back-link"
          >
            <ArrowLeft className={`h-4 w-4 me-2 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            {t('common.back')}
          </Link>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] mb-4">
            {t('training.requestCustom')}
          </h1>
          <p className="text-lg text-[#6B7280] max-w-2xl">
            {t('training.customTrainingDesc')}
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white border-b border-[#E5E7EB]">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                <BookOpen className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#111827] mb-1">Tailored Content</h3>
                <p className="text-sm text-[#6B7280]">Programs designed specifically for your organization's needs and goals.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                <Users className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#111827] mb-1">Expert Trainers</h3>
                <p className="text-sm text-[#6B7280]">Industry professionals with hands-on experience in their fields.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                <Award className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <h3 className="font-semibold text-[#111827] mb-1">Certified Programs</h3>
                <p className="text-sm text-[#6B7280]">Accredited training with certificates upon completion.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container-custom max-w-3xl">
          <form onSubmit={handleSubmit}>
            {/* Honeypot anti-spam field */}
            <HoneypotField 
              value={formData.website} 
              onChange={(v) => handleChange('website', v)} 
            />
            
            <Card>
              <CardHeader>
                <CardTitle>Organization Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="organization_name">{t('forms.organization')} *</Label>
                    <Input
                      id="organization_name"
                      value={formData.organization_name}
                      onChange={(e) => handleChange('organization_name', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-organization"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_person">{t('forms.contactPerson')} *</Label>
                    <Input
                      id="contact_person"
                      value={formData.contact_person}
                      onChange={(e) => handleChange('contact_person', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-contact-person"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('forms.email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('forms.phone')} *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">{t('forms.country')} *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-country"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="industry">{t('forms.industry')}</Label>
                    <Input
                      id="industry"
                      value={formData.industry}
                      onChange={(e) => handleChange('industry', e.target.value)}
                      className="input-focus"
                      data-testid="input-industry"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Training Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="topics">{t('forms.topics')} *</Label>
                  <Input
                    id="topics"
                    value={formData.topics}
                    onChange={(e) => handleChange('topics', e.target.value)}
                    placeholder="Leadership, Project Management, Communication Skills..."
                    required
                    className="input-focus"
                    data-testid="input-topics"
                  />
                  <p className="text-xs text-[#6B7280]">Separate multiple topics with commas</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="audience_size">{t('forms.audienceSize')}</Label>
                    <Input
                      id="audience_size"
                      type="number"
                      min="1"
                      value={formData.audience_size}
                      onChange={(e) => handleChange('audience_size', e.target.value)}
                      className="input-focus"
                      data-testid="input-audience-size"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('training.language')} *</Label>
                    <Select value={formData.language} onValueChange={(v) => handleChange('language', v)}>
                      <SelectTrigger data-testid="select-language">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="english">{t('training.english')}</SelectItem>
                        <SelectItem value="arabic">{t('training.arabic')}</SelectItem>
                        <SelectItem value="bilingual">{t('training.bilingual')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="preferred_dates">{t('forms.preferredDates')}</Label>
                    <Input
                      id="preferred_dates"
                      value={formData.preferred_dates}
                      onChange={(e) => handleChange('preferred_dates', e.target.value)}
                      placeholder="Q1 2024, March 2024, Flexible..."
                      className="input-focus"
                      data-testid="input-preferred-dates"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preferred_location">{t('forms.preferredLocation')} *</Label>
                    <Input
                      id="preferred_location"
                      value={formData.preferred_location}
                      onChange={(e) => handleChange('preferred_location', e.target.value)}
                      placeholder="Frankfurt, Dubai, Online..."
                      required
                      className="input-focus"
                      data-testid="input-preferred-location"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="budget_min">{t('forms.budget')} Min</Label>
                    <Input
                      id="budget_min"
                      type="number"
                      min="0"
                      value={formData.budget_min}
                      onChange={(e) => handleChange('budget_min', e.target.value)}
                      className="input-focus"
                      data-testid="input-budget-min"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget_max">{t('forms.budget')} Max</Label>
                    <Input
                      id="budget_max"
                      type="number"
                      min="0"
                      value={formData.budget_max}
                      onChange={(e) => handleChange('budget_max', e.target.value)}
                      className="input-focus"
                      data-testid="input-budget-max"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Select value={formData.currency} onValueChange={(v) => handleChange('currency', v)}>
                      <SelectTrigger data-testid="select-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="AED">AED</SelectItem>
                        <SelectItem value="SAR">SAR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="training_goals">{t('forms.trainingGoals')}</Label>
                  <Textarea
                    id="training_goals"
                    value={formData.training_goals}
                    onChange={(e) => handleChange('training_goals', e.target.value)}
                    rows={4}
                    placeholder="What outcomes do you expect from this training program?"
                    className="input-focus"
                    data-testid="input-training-goals"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">{t('forms.notes')}</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    rows={3}
                    className="input-focus"
                    data-testid="input-notes"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 p-3 bg-red-50 rounded-lg">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={submitting}
                  data-testid="submit-btn"
                >
                  {submitting ? t('forms.sending') : t('forms.submit')}
                </Button>
              </CardContent>
            </Card>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CustomizedTrainingRequestPage;
